<!-- Where the gain came from: P&L broken down into prices, exchange rates, interest, dividends and costs. -->
<script lang="ts">
  import * as E from "../core/engine";
  import * as X from "../core/analysis";
  import type { AttributionRow } from "../core/types";
  import { app, need, PERIOD_NAMES } from "../lib/state.svelte";
  import { esc, fmtNum, heat, pctPlain, rangeLabel } from "../lib/format";
  import { baseOption, compactMoney, palette, series, tipMoney, tooltipRows, valueAxis, type Opt, type TipParam } from "../lib/chart";
  import { BOND_KIND_NAME } from "../lib/holdings";
  import ViewHead from "../components/ViewHead.svelte";
  import Kpi from "../components/Kpi.svelte";
  import Card from "../components/Card.svelte";
  import Chart from "../components/Chart.svelte";
  import Seg from "../components/Seg.svelte";
  import Money from "../components/Money.svelte";
  import Signed from "../components/Signed.svelte";
  import Pct from "../components/Pct.svelte";

  const m = $derived(need(app.model));
  const A = $derived(need(app.agg));
  const r = $derived(app.range);
  const st = $derived(E.periodStats(m, A, r.start, r.end));
  const at = $derived(X.attribution(m, app.accList, st.s0, st.e));
  const B = $derived(at.B);
  const fxTotal = $derived(B.fx + B.fxCash);
  const other = $derived.by(() => {
    const known = B.price + fxTotal + B.bond + B.dividends + B.cashInterest + B.fees + B.taxes + B.other;
    return st.gross - known + B.other;
  });
  const taxStep = $derived(app.tax ? -st.taxChange : null);
  const steps = $derived(
    [
      { k: "price", name: "Zmiana cen akcji i ETF", v: B.price, always: true },
      { k: "fx", name: "Kursy walut", v: fxTotal, always: true },
      { k: "bond", name: "Odsetki z obligacji", v: B.bond },
      { k: "div", name: "Dywidendy", v: B.dividends },
      { k: "cint", name: "Odsetki od gotówki", v: B.cashInterest },
      { k: "fees", name: "Prowizje i opłaty", v: B.fees, always: true },
      { k: "tax", name: "Podatki pobrane u źródła", v: B.taxes },
      { k: "other", name: "Inne / niewyjaśnione", v: other, tiny: true },
    ].filter((s) => s.always || Math.abs(s.v) >= (s.tiny ? 10 : 0.005)),
  );
  const pname = $derived(PERIOD_NAMES[app.period]);
  const fxList = $derived(Object.entries(at.fxByCcy).filter(([, v]) => Math.abs(v) >= 1).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1])));

  // year by year
  interface YearRow { label: string; gross: number; price: number; fx: number; bond: number; income: number; costs: number; other: number; belka: number }
  const yearly = $derived.by(() => {
    const y0 = +r.start.slice(0, 4), y1 = +r.end.slice(0, 4);
    const out: YearRow[] = [];
    if (y1 > y0) {
      for (let y = y0; y <= y1; y++) {
        const ys = y === y0 ? r.start : `${y}-01-01`, ye = y === y1 ? r.end : `${y}-12-31`;
        const s2 = E.periodStats(m, A, ys, ye);
        const a2 = X.attribution(m, app.accList, s2.s0, s2.e).B;
        const k2 = a2.price + a2.fx + a2.fxCash + a2.bond + a2.dividends + a2.cashInterest + a2.fees + a2.taxes + a2.other;
        out.push({ label: String(y) + (ys !== `${y}-01-01` || (ye !== `${y}-12-31` && y !== +m.today.slice(0, 4)) ? "*" : ""), gross: s2.gross, price: a2.price, fx: a2.fx + a2.fxCash, bond: a2.bond,
          income: a2.dividends + a2.cashInterest, costs: a2.fees + a2.taxes, other: s2.gross - k2 + a2.other, belka: app.tax ? -s2.taxChange : 0 });
      }
    }
    return out;
  });

  // contribution to return in pp
  const contribBy = $derived(app.ui.contribBy || "instrument");
  const ct = $derived(X.contributions(m, A, st.s0, st.e));
  const cRows = $derived.by(() => {
    const cMap: Record<string, number> = {};
    for (const x of ct.rows) {
      const k = contribBy === "group" ? x.group : x.isBond && BOND_KIND_NAME[x.bondKind] ? "Obligacje " + BOND_KIND_NAME[x.bondKind] : x.name;
      cMap[k] = (cMap[k] || 0) + x.pp;
    }
    if (Math.abs(ct.other) > 0.00005) cMap["Gotówka, kursy gotówki, opłaty"] = (cMap["Gotówka, kursy gotówki, opłaty"] || 0) + ct.other;
    return Object.entries(cMap).filter(([, v]) => Math.abs(v) >= 0.00005).sort((a, b) => b[1] - a[1]);
  });

  // asset groups year by year
  const gy = $derived(X.groupYearReturns(m, A, st.s0, st.e));
  const pYears = $derived(E.periodicReturns(m, A, "year", false, r));
  const gNames = $derived(Object.keys(gy).filter((g) => Object.keys(gy[g]).length).sort());
  const best = (y: string): string | null => {
    let b: string | null = null;
    for (const g of gNames) { const v = gy[g][y]?.ret; if (v !== undefined && (b === null || v > gy[b][y].ret)) b = g; }
    return b;
  };

  // instruments (bonds collapsed by kind)
  type InstrRow = Omit<AttributionRow, "bondKind"> & { n?: number };
  const shown = $derived.by(() => {
    const bondGroups: Record<string, InstrRow & { n: number }> = {};
    const rows: InstrRow[] = [];
    for (const x of at.rows) {
      if (x.isBond && BOND_KIND_NAME[x.bondKind]) {
        const g = (bondGroups[x.bondKind] ||= { key: "BONDS-" + x.bondKind, name: "Obligacje " + BOND_KIND_NAME[x.bondKind], isBond: true, price: 0, fx: 0, income: 0, fees: 0, taxes: 0, total: 0, n: 0 });
        for (const k of ["price", "fx", "income", "fees", "taxes", "total"] as const) g[k] += x[k];
        g.n++;
      } else rows.push(x);
    }
    rows.push(...Object.values(bondGroups));
    return rows.filter((x) => Math.abs(x.total) >= 0.5 || Math.abs(x.price) >= 0.5 || Math.abs(x.fx) >= 0.5).sort((a, b) => b.total - a.total);
  });
  const sum = $derived(shown.reduce((s, x) => ({ price: s.price + x.price, fx: s.fx + x.fx, income: s.income + x.income, fees: s.fees + x.fees, taxes: s.taxes + x.taxes, total: s.total + x.total }), { price: 0, fx: 0, income: 0, fees: 0, taxes: 0, total: 0 }));

  // waterfall: floating bars (custom series), total at the end
  const waterOpt = $derived.by(() => {
    const p = palette();
    const privacy = app.privacy;
    const cats: string[] = [];
    const data: { value: [number, number, number, number]; total: boolean }[] = [];
    let cum = 0;
    const push = (name: string, v: number, total = false) => {
      const a0 = total ? 0 : cum, a1 = total ? v : cum + v;
      cats.push(name);
      data.push({ value: [cats.length - 1, a0, a1, v], total });
      if (!total) cum += v;
    };
    for (const s of steps) push(s.name, s.v);
    push("Wynik brutto", st.gross, true);
    if (app.tax) { cum = st.gross; push("Belka należna i odroczona", taxStep as number); push("Wynik po podatku", st.net, true); }
    return {
      ...baseOption(p), legend: { show: false }, grid: { left: 8, right: 18, top: 24, bottom: 6, containLabel: true },
      tooltip: { trigger: "item", backgroundColor: p.surface, borderColor: p.border, textStyle: { color: p.ink }, formatter: (x: TipParam) => `${esc(cats[x.value[0]])}<br><b>${x.data.total ? "" : x.value[3] >= 0 ? "+" : "−"}${tipMoney(Math.abs(x.value[3]))}</b>` },
      xAxis: { type: "category", data: cats, axisLabel: { color: p.ink2, interval: 0, width: 96, overflow: "break", fontSize: 11 }, axisTick: { show: false }, axisLine: { lineStyle: { color: p.axis } } },
      yAxis: valueAxis(compactMoney, p),
      series: [{
        type: "custom", data,
        renderItem: (params: TipParam, api: TipParam) => {
          const i = api.value(0), y0 = api.value(1), y1 = api.value(2), v = api.value(3);
          const tot = data[params.dataIndex].total;
          const a = api.coord([i, y0]), b = api.coord([i, y1]);
          const w = api.size([1, 0])[0] * 0.56;
          const color = tot ? p.accent : v >= 0 ? p.gain : p.loss;
          const top = Math.min(a[1], b[1]), h = Math.max(1, Math.abs(a[1] - b[1]));
          const children: Opt[] = [{ type: "rect", shape: { x: a[0] - w / 2, y: top, width: w, height: h, r: 3 }, style: { fill: color } }];
          if (!privacy) children.push({ type: "text", style: { text: (tot ? "" : v >= 0 ? "+" : "−") + compactMoney(Math.abs(v)), x: a[0], y: top - 4, align: "center", verticalAlign: "bottom", fill: p.ink2, font: "11px system-ui, sans-serif" } });
          // connector to the next bar
          if (params.dataIndex < data.length - 1) {
            const nx = api.coord([i + 1, y1]);
            children.push({ type: "line", shape: { x1: a[0] + w / 2, y1: b[1], x2: nx[0] - w / 2, y2: b[1] }, style: { stroke: p.axis, lineDash: [3, 3], lineWidth: 1 } });
          }
          return { type: "group", children };
        },
        encode: { x: 0, y: [1, 2] },
      }],
    };
  });

  const contribOpt = $derived.by(() => {
    const p = palette();
    const avgCap = ct.avgCap;
    return {
      ...baseOption(p), legend: { show: false }, grid: { left: 24, right: 44, top: 8, bottom: 6, containLabel: true },
      tooltip: { trigger: "item", backgroundColor: p.surface, borderColor: p.border, textStyle: { color: p.ink }, formatter: (x: TipParam) => `${esc(x.name)}<br><b>${x.value >= 0 ? "+" : "−"}${fmtNum(Math.abs(x.value) * 100, 2)} pp</b> · ${tipMoney(x.value * avgCap)}` },
      xAxis: valueAxis((v) => fmtNum(v * 100, 1) + " pp", p, { splitNumber: 4 }),
      yAxis: { type: "category", inverse: true, data: cRows.map((x) => x[0]), axisLabel: { color: p.ink2, width: 210, overflow: "truncate" }, axisTick: { show: false }, axisLine: { lineStyle: { color: p.axis } } },
      series: [{ type: "bar", barMaxWidth: 16, label: { show: true, position: "right", color: p.ink2, fontSize: 11, formatter: (x: TipParam) => `${x.value >= 0 ? "+" : "−"}${fmtNum(Math.abs(x.value) * 100, 1)}` },
        data: cRows.map(([, v]) => ({ value: +v.toFixed(6), itemStyle: { color: v >= 0 ? p.gain : p.loss, borderRadius: v >= 0 ? [0, 4, 4, 0] : [4, 0, 0, 4] } })) }],
    };
  });

  const yearsOpt = $derived.by(() => {
    const p = palette();
    const comp: [keyof YearRow, string, string][] = [["price", "Zmiana cen", series(0)], ["fx", "Kursy walut", series(3)], ["bond", "Odsetki z obligacji", series(1)], ["income", "Dywidendy i odsetki", series(2)], ["costs", "Prowizje i podatki u źródła", series(5)], ["other", "Inne", p.muted]];
    if (app.tax) comp.push(["belka", "Belka należna i odroczona", series(6)]);
    const num = (y: YearRow, k: keyof YearRow) => y[k] as number;
    return {
      ...baseOption(p), tooltip: { ...baseOption(p).tooltip, axisPointer: { type: "shadow" }, formatter: (ps: TipParam) => tooltipRows(ps, (v) => `${v >= 0 ? "+" : "−"}${tipMoney(Math.abs(v))}`) },
      xAxis: { type: "category", data: yearly.map((y) => y.label), axisTick: { show: false }, axisLine: { lineStyle: { color: p.axis } }, axisLabel: { color: p.muted } },
      yAxis: valueAxis(compactMoney, p),
      series: [
        ...comp.filter(([k]) => yearly.some((y) => Math.abs(num(y, k)) >= 1)).map(([k, name, color]) => ({ name, type: "bar", stack: "a", barMaxWidth: 34, data: yearly.map((y) => +num(y, k).toFixed(2)), itemStyle: { color } })),
        { name: app.tax ? "Wynik po podatku" : "Wynik brutto", type: "line", data: yearly.map((y) => +(y.gross + y.belka).toFixed(2)), symbol: "circle", symbolSize: 7, lineStyle: { width: 0 }, itemStyle: { color: p.ink } },
      ],
    };
  });
</script>

<ViewHead title="Skąd wziął się zysk" sub="{rangeLabel(r)} · {pname}" />
<div class="kpis">
  <Kpi label="Wynik brutto: {pname}">
    {#snippet value()}<Signed v={st.gross} />{/snippet}
    {#snippet sub()}zmiana wartości <Signed v={st.endValue - st.startValue} /> − wpłaty netto <Money v={st.flows} />{/snippet}
  </Kpi>
  <Kpi label="Zmiana cen akcji i ETF" sub="w walucie notowań, przeliczona po kursie z dnia">{#snippet value()}<Signed v={B.price} />{/snippet}</Kpi>
  <Kpi label="Kursy walut">
    {#snippet value()}<Signed v={fxTotal} />{/snippet}
    {#snippet sub()}{#each fxList as [c, v], i (c)}{#if i}{" · "}{/if}{c} <Signed {v} />{:else}brak papierów w walutach{/each}{/snippet}
  </Kpi>
  <Kpi label="Obligacje, dywidendy, odsetki">
    {#snippet value()}<Signed v={B.bond + B.dividends + B.cashInterest} />{/snippet}
    {#snippet sub()}obligacje <Signed v={B.bond} /> · dywidendy <Signed v={B.dividends} />{/snippet}
  </Kpi>
  <Kpi label="Koszty i podatki">
    {#snippet value()}<Signed v={B.fees + B.taxes + (taxStep ?? 0)} />{/snippet}
    {#snippet sub()}prowizje <Signed v={B.fees} /> · podatki u źródła <Signed v={B.taxes} />{#if app.tax}{" "}· Belka <Signed v={taxStep} />{/if}{/snippet}
  </Kpi>
  {#if app.tax}
    <Kpi label="Wynik po podatku" sub="po Belce należnej i odroczonej">{#snippet value()}<Signed v={st.net} />{/snippet}</Kpi>
  {/if}
</div>
<Card title="Wodospad wyniku" hint="kursy walut: zmiana wartości papierów i gotówki w walutach wynikająca wyłącznie ze zmiany kursu EUR/USD/… względem złotego; odsetki z obligacji: narastająca wartość i kupony">
  <Chart id="chWater" class="tall" option={waterOpt} />
</Card>
{#if yearly.length}
  <Card title="Źródła wyniku rok po roku" hint="* rok przycięty do wybranego zakresu"><Chart id="chYears" option={yearsOpt} /></Card>
{/if}
<Card hint="punkty procentowe = wynik składnika w zł / średni zaangażowany kapitał okresu ({compactMoney(ct.avgCap)} zł); uwzględnia wagę składnika w portfelu, przed Belką">
  {#snippet title()}Wkład do wyniku: <Pct v={ct.total} d={1} /> średniego kapitału{/snippet}
  {#snippet extra()}
    <Seg id="contribSeg" attr="c" options={[["instrument", "Instrumenty"], ["group", "Grupy"]] as const} value={contribBy} onselect={(k) => (app.ui.contribBy = k)} />
  {/snippet}
  <Chart id="chContrib" style="height:{Math.max(220, cRows.length * 26 + 40)}px" option={contribOpt} />
</Card>
{#if gNames.length && pYears.length}
  <Card title="Która grupa aktywów wygrała w danym roku" hint="stopa zwrotu w PLN (z kursem walut) części portfela w danej grupie, liczona tylko wtedy, gdy ją miałeś · pogrubienie = najlepsza grupa roku · * rok niepełny">
    <div class="table-wrap"><table class="heat"><thead><tr><th class="l">Rok</th>{#each gNames as g (g)}<th>{g}</th>{/each}<th>Portfel</th><th>Inflacja</th></tr></thead><tbody>
      {#each pYears.slice().reverse() as py (py.label)}
        {@const y = py.label}
        {@const b = best(y)}
        <tr><td class="l"><b>{y}</b>{#if py.partial || py.clipped}{" "}<span class="muted small">*</span>{/if}</td>
          {#each gNames as g (g)}{@const o = gy[g][y]}<td class="cell" style={heat(o?.ret, 0.3)}>{#if o}{#if g === b}<b>{pctPlain(o.ret)}</b>{:else}{pctPlain(o.ret)}{/if}{/if}</td>{/each}
          <td class="cell" style={heat(py.nominal, 0.3)}><b>{pctPlain(py.nominal)}</b></td><td class="cell muted">{py.inflation !== null ? pctPlain(py.inflation) : "–"}</td></tr>
      {/each}
    </tbody></table></div>
  </Card>
{/if}
<Card title="Instrumenty: cena, kurs waluty, dochody" hint="zmiana ceny obejmuje różnicę między ceną transakcji a kursem zamknięcia dnia; dla obligacji — narastanie wartości">
  <div class="table-wrap"><table><thead><tr><th class="l">Instrument</th><th>Zmiana ceny</th><th>Kurs waluty</th><th>Dywidendy / kupony</th><th>Prowizje</th><th>Podatki u źródła</th><th>Razem</th><th>Udział kursu</th></tr></thead><tbody>
    {#each shown as x, i (i)}
      <tr><td class="l">{x.name}{x.n ? " " : ""}{#if x.n}<span class="muted small">{x.n} serii</span>{/if}</td><td><Signed v={x.price} /></td><td>{#if x.isBond}–{:else}<Signed v={x.fx} />{/if}</td><td>{#if x.income}<Signed v={x.income} />{:else}–{/if}</td><td>{#if x.fees}<Signed v={x.fees} />{:else}–{/if}</td><td>{#if x.taxes}<Signed v={x.taxes} />{:else}–{/if}</td><td><b><Signed v={x.total} /></b></td>
        <td>{!x.isBond && Math.abs(x.price) + Math.abs(x.fx) > 1 ? fmtNum((Math.abs(x.fx) / (Math.abs(x.price) + Math.abs(x.fx))) * 100, 0) + "%" : "–"}</td></tr>
    {/each}
    </tbody><tfoot><tr class="total"><td class="l">Razem instrumenty</td><td><Signed v={sum.price} /></td><td><Signed v={sum.fx} /></td><td><Signed v={sum.income} /></td><td><Signed v={sum.fees} /></td><td><Signed v={sum.taxes} /></td><td><Signed v={sum.total} /></td><td></td></tr>
    {#if Math.abs(B.fxCash) >= 0.5}<tr class="sub"><td class="l">Gotówka w walutach (kurs)</td><td></td><td><Signed v={B.fxCash} /></td><td colspan="5"></td></tr>{/if}
    {#if Math.abs(B.cashInterest) >= 0.5}<tr class="sub"><td class="l">Odsetki od gotówki</td><td></td><td></td><td><Signed v={B.cashInterest} /></td><td colspan="4"></td></tr>{/if}
    </tfoot></table></div>
</Card>
